// AddRecipe.tsx
import React, { useState, useRef } from 'react';
import { PlusCircle, MinusCircle, Upload, Image as ImageIcon } from 'lucide-react';
import { Recipe, Ingredient } from '../types';
import { toast } from 'react-hot-toast';
import { importRecipeFromUrl } from '../lib/recipe-import';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

interface AddRecipeProps {
  onAddRecipe: (recipe: Recipe) => void;
}

export default function AddRecipe({ onAddRecipe }: AddRecipeProps) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [recipe, setRecipe] = useState<Partial<Recipe>>({
    name: '',
    ingredients: [],
    instructions: [],
    prepTime: 0,
    cookTime: 0,
    servings: 1,
    tags: [],
  });

  const [currentIngredient, setCurrentIngredient] = useState<Partial<Ingredient>>({
    name: '',
    amount: 0,
    unit: '',
  });

  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleAddIngredient = () => {
    if (currentIngredient.name && currentIngredient.amount && currentIngredient.unit) {
      setRecipe((prev) => ({
        ...prev,
        ingredients: [...(prev.ingredients || []), currentIngredient as Ingredient],
      }));
      setCurrentIngredient({ name: '', amount: 0, unit: '' });
    } else {
      toast.error('Please fill out all ingredient fields');
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      setUploadingImage(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('recipe-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('recipe-images')
        .getPublicUrl(filePath);

      setRecipe((prev) => ({ ...prev, imageUrl: data.publicUrl }));
      toast.success('Image uploaded successfully!');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      handleImageUpload(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (recipe.name && recipe.ingredients?.length && recipe.instructions?.length) {
      try {
        const { data: newRecipe, error } = await supabase
          .from('recipes')
          .insert({
            name: recipe.name,
            ingredients: recipe.ingredients,
            instructions: recipe.instructions,
            prep_time: recipe.prepTime || 0,
            cook_time: recipe.cookTime || 0,
            servings: recipe.servings || 1,
            image_url: recipe.imageUrl,
            source: recipe.source,
            tags: recipe.tags,
            user_id: user?.id,
          })
          .select('*, image_url')
          .single();

        if (error) throw error;

        onAddRecipe(newRecipe as Recipe);
        toast.success('Recipe added successfully!');
        setRecipe({
          name: '',
          ingredients: [],
          instructions: [],
          prepTime: 0,
          cookTime: 0,
          servings: 1,
          tags: [],
        });
      } catch (error: any) {
        console.error('Error adding recipe:', error);
        toast.error('Failed to add recipe');
      }
    } else {
      toast.error('Please fill in the required fields');
    }
  };

  const handleImport = async () => {
    if (!importUrl) {
      toast.error('Please enter a URL to import');
      return;
    }

    setImporting(true);
    try {
      const importedRecipe = await importRecipeFromUrl(importUrl);
      setRecipe(importedRecipe);
      setImportUrl('');
    } catch (error: any) {
      console.error('Import failed:', error);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Import Recipe Section */}
      <div className="mb-4 lg:mb-8 p-4 lg:p-6 bg-white rounded-xl shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium mb-4">Import Recipe</h3>
        <div className="flex flex-col lg:flex-row gap-4">
          <input
            type="url"
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="Paste recipe URL..."
            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            onClick={handleImport}
            disabled={importing || !importUrl}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Upload className="h-5 w-5" />
            {importing ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>

      {/* Add Recipe Form */}
      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200">
        {/* Recipe Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recipe Name
          </label>
          <input
            type="text"
            value={recipe.name}
            onChange={(e) => setRecipe({ ...recipe, name: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
          />
        </div>

        {/* Recipe Image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recipe Image
          </label>
          <div className="mt-1 flex items-center gap-4">
            <input
              type="url"
              value={recipe.imageUrl || ''}
              onChange={(e) => setRecipe({ ...recipe, imageUrl: e.target.value })}
              placeholder="Image URL"
              className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <span className="text-gray-500">or</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 disabled:opacity-50"
            >
              <ImageIcon className="h-5 w-5" />
              {uploadingImage ? 'Uploading...' : 'Upload'}
            </button>
          </div>
          {recipe.imageUrl && (
            <div className="mt-2">
              <img
                src={recipe.imageUrl}
                alt="Recipe preview"
                className="w-32 h-32 object-cover rounded-lg"
              />
            </div>
          )}
        </div>

        {/* Ingredients */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ingredients
          </label>
          <div className="space-y-2">
            {recipe.ingredients?.map((ingredient, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="flex-1">
                  {ingredient.amount} {ingredient.unit} {ingredient.name}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setRecipe((prev) => ({
                      ...prev,
                      ingredients: prev.ingredients?.filter((_, i) => i !== index),
                    }))
                  }
                  className="text-red-500 hover:text-red-700"
                >
                  <MinusCircle className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={currentIngredient.name}
              onChange={(e) =>
                setCurrentIngredient((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Ingredient name"
              className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <input
              type="number"
              value={currentIngredient.amount || ''}
              onChange={(e) =>
                setCurrentIngredient((prev) => ({
                  ...prev,
                  amount: parseFloat(e.target.value),
                }))
              }
              placeholder="Amount"
              className="w-32 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <input
              type="text"
              value={currentIngredient.unit}
              onChange={(e) =>
                setCurrentIngredient((prev) => ({ ...prev, unit: e.target.value }))
              }
              placeholder="Unit"
              className="w-24 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              type="button"
              onClick={handleAddIngredient}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <PlusCircle className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Instructions
          </label>
          <textarea
            value={recipe.instructions?.join('\n')}
            onChange={(e) =>
              setRecipe({ ...recipe, instructions: e.target.value.split('\n') })
            }
            className="w-full px-4 py-2 border rounded-lg h-32 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter instructions (one per line)"
            required
          />
        </div>

        {/* Prep Time, Cook Time, Servings */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prep Time (min)
            </label>
            <input
              type="number"
              value={recipe.prepTime}
              onChange={(e) =>
                setRecipe({ ...recipe, prepTime: parseInt(e.target.value) })
              }
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cook Time (min)
            </label>
            <input
              type="number"
              value={recipe.cookTime}
              onChange={(e) =>
                setRecipe({ ...recipe, cookTime: parseInt(e.target.value) })
              }
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Servings
            </label>
            <input
              type="number"
              value={recipe.servings}
              onChange={(e) =>
                setRecipe({ ...recipe, servings: parseInt(e.target.value) })
              }
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
              min="1"
            />
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tags (comma-separated)
          </label>
          <input
            type="text"
            value={recipe.tags?.join(', ')}
            onChange={(e) =>
              setRecipe({
                ...recipe,
                tags: e.target.value.split(',').map((tag) => tag.trim()),
              })
            }
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <button
          type="submit"
          className="w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-lg font-medium"
        >
          Add Recipe
        </button>
      </form>
    </div>
  );
}
